// Ported from horizontal-actions-dashboard/frontend/src/pages/TerminalOverviewPage.tsx
// (+ features/terminals/TerminalsSection.tsx merged into one page, MUI-styled).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { getTerminals, ApiError } from '../api/horizontalActionsApi';
import type { TerminalSummary } from '../api/horizontalActionsTypes';
import { TerminalsHeader } from './terminals-shared/TerminalsHeader';
import { useDashboardChanges } from '../hooks/useDashboardChanges';
import { TerminalsLoadingState, TerminalsErrorState } from './terminals-shared/TerminalsStateViews';
import { TerminalsProgressBar } from './terminals-shared/TerminalsProgressBar';
import { TerminalsRiskScoreCell, TerminalsRiskScoreLegend } from './terminals-shared/TerminalsRiskScore';
import { groupByRegion, RegionGroup } from '../utils/horizontalActionsFormat';

const SORT_OPTIONS = [
  { value: 'name', label: 'Terminal name' },
  { value: 'region', label: 'Region' },
  { value: 'completion_asc', label: 'Lowest completion first' },
  { value: 'completion_desc', label: 'Highest completion first' },
  { value: 'open_high_desc', label: 'Most open High actions first' },
];

function RegionTerminalTable({ group }: { group: RegionGroup<TerminalSummary> }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        <Link to={`/terminals/regions/${group.regionCode}`}>{group.regionDisplayName}</Link>{' '}
        <Typography component="span" variant="body2" sx={{ color: '#5a6b7c' }}>
          ({group.items.length} terminal{group.items.length === 1 ? '' : 's'})
        </Typography>
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Terminal</TableCell>
              <TableCell>Completion</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>Total actions</TableCell>
              <TableCell>Open</TableCell>
              <TableCell>Overdue Actions</TableCell>
              <TableCell>Open High</TableCell>
              <TableCell>Risk Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.items.map((t) => (
              <TableRow key={t.terminal_name} hover>
                <TableCell component="th" scope="row">
                  <Link to={`/terminals/${encodeURIComponent(t.terminal_name)}`}>{t.terminal_name}</Link>
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <TerminalsProgressBar pct={t.kpi_completion_pct} label={`${t.terminal_name} completion`} />
                </TableCell>
                <TableCell>{t.kpi_completed}</TableCell>
                <TableCell>{t.kpi_total}</TableCell>
                <TableCell>{t.kpi_in_progress}</TableCell>
                <TableCell>{t.kpi_overdue > 0 ? <strong>{t.kpi_overdue}</strong> : 0}</TableCell>
                <TableCell>{t.kpi_open_high > 0 ? <strong>{t.kpi_open_high}</strong> : 0}</TableCell>
                <TerminalsRiskScoreCell score={t.risk_score} band={t.risk_band} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function TerminalsOverview() {
  const [sort, setSort] = useState('completion_asc');
  const [data, setData] = useState<TerminalSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const loaded = useRef(false);

  // Only the first load shows the loading state or the full-page error. Later loads (live
  // updates, Refresh, a new sort order) are silent: the tables stay on screen, and a failed
  // refresh keeps what is shown and says so.
  const loadData = useCallback(async () => {
    const firstLoad = !loaded.current;
    if (firstLoad) setLoading(true);
    try {
      const result = await getTerminals(sort);
      setData(result);
      loaded.current = true;
      setError(null);
      setRefreshFailed(false);
    } catch (err) {
      if (firstLoad) setError(err instanceof ApiError ? err : new Error('Failed to load terminals.'));
      else setRefreshFailed(true);
    } finally {
      if (firstLoad) setLoading(false);
    }
  }, [sort]);

  // Reload when the server reports a Jira or register change.
  useDashboardChanges(() => { void loadData(); });

  useEffect(() => {
    loadData();
  }, [loadData]);

  const groups = useMemo(() => (data ? groupByRegion(data) : []), [data]);

  return (
    <Box sx={{ p: 3 }}>
      <TerminalsHeader title="Terminals — Terminal Overview" onRefreshed={loadData} />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6">Terminal overview</Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="terminal-sort-label">Sort by</InputLabel>
            <Select
              labelId="terminal-sort-label"
              label="Sort by"
              value={sort}
              onChange={(e) => setSort(e.target.value as string)}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {data && data.length > 0 && <TerminalsRiskScoreLegend inline />}

        {loading && <TerminalsLoadingState label="Loading terminals…" />}
        {error && <TerminalsErrorState error={error} onRetry={loadData} />}
        {refreshFailed && (
          <Alert severity="warning" role="alert" sx={{ mb: 2 }} onClose={() => setRefreshFailed(false)}>
            Could not refresh. The figures shown may be out of date.
          </Alert>
        )}
        {!loading && !error && data && data.length === 0 && <Typography role="status">No terminals found.</Typography>}

        {!loading && !error && groups.map((group) => <RegionTerminalTable key={group.regionCode} group={group} />)}
      </Paper>
    </Box>
  );
}
